import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, DollarSign, Eye, MousePointer, Plus, Settings,
  Loader2, TrendingUp, Image, Link2, Trash2, Pause, Play,
  CreditCard, AlertCircle, Check, ArrowLeft, LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdvertiserPortal - Main advertiser dashboard
 */
export default function AdvertiserPortal() {
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Get token from localStorage
  const getToken = () => localStorage.getItem('advertiser_token');
  const getHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
  
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/advertiser/login');
      return;
    }
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [profileRes, adsRes] = await Promise.all([
        axios.get(`${API}/advertising/me`, { headers: getHeaders() }),
        axios.get(`${API}/advertising/ads`, { headers: getHeaders() })
      ]);
      setAdvertiser(profileRes.data);
      setAds(adsRes.data.ads || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('advertiser_token');
        navigate('/advertiser/login');
      }
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('advertiser_token');
    navigate('/advertiser/login');
  };
  
  const handleDeleteAd = async (adId) => {
    if (!confirm('Delete this ad?')) return;
    
    try {
      await axios.delete(`${API}/advertising/ads/${adId}`, { headers: getHeaders() });
      toast.success('Ad deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete ad');
    }
  };
  
  const handleToggleAd = async (adId, currentStatus) => {
    try {
      await axios.put(`${API}/advertising/ads/${adId}`, {
        is_active: !currentStatus
      }, { headers: getHeaders() });
      toast.success(currentStatus ? 'Ad paused' : 'Ad activated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update ad');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : 0;
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="advertiser-portal">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-bold text-lg text-gray-900">Advertiser Portal</h1>
                <p className="text-xs text-gray-500">{advertiser?.company_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-green-50 text-green-700">
                <DollarSign className="w-3 h-3 mr-1" />
                ${advertiser?.balance?.toFixed(2) || '0.00'}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="ads">My Ads</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>
          
          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalImpressions.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Impressions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <MousePointer className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalClicks.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Clicks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{avgCtr}%</p>
                      <p className="text-xs text-gray-500">CTR</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">${advertiser?.total_spent?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-500">Total Spent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Quick Actions */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowCreateAd(true)}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Plus className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Create New Ad</h3>
                    <p className="text-sm text-gray-500">Reach more customers</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('billing')}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Add Funds</h3>
                    <p className="text-sm text-gray-500">Balance: ${advertiser?.balance?.toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Ads Tab */}
          <TabsContent value="ads">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">My Advertisements</h2>
              <Button onClick={() => setShowCreateAd(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Ad
              </Button>
            </div>
            
            {ads.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="font-semibold text-gray-900">No ads yet</h3>
                  <p className="text-gray-500 mb-4">Create your first ad to start reaching customers</p>
                  <Button onClick={() => setShowCreateAd(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Ad
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {ads.map((ad) => (
                  <Card key={ad.ad_id}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {ad.image_url && (
                          <img
                            src={ad.image_url}
                            alt=""
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold truncate">{ad.title}</h3>
                              <p className="text-sm text-gray-500 line-clamp-2">{ad.description}</p>
                            </div>
                            <Badge variant={ad.status === 'approved' ? 'default' : 'secondary'}>
                              {ad.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span>{ad.impressions || 0} impressions</span>
                            <span>{ad.clicks || 0} clicks</span>
                            <span>${ad.total_spent?.toFixed(2) || '0.00'} spent</span>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleAd(ad.ad_id, ad.is_active)}
                            >
                              {ad.is_active ? (
                                <><Pause className="w-3 h-3 mr-1" /> Pause</>
                              ) : (
                                <><Play className="w-3 h-3 mr-1" /> Activate</>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteAd(ad.ad_id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Billing Tab */}
          <TabsContent value="billing">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Add Funds</CardTitle>
                  <CardDescription>Add money to your advertising balance</CardDescription>
                </CardHeader>
                <CardContent>
                  <AddFundsForm onSuccess={fetchData} getHeaders={getHeaders} />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Pricing</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span>Cost per 1,000 impressions (CPM)</span>
                    <span className="font-semibold">$2.00</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Cost per click (CPC)</span>
                    <span className="font-semibold">$0.25</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Minimum daily budget</span>
                    <span className="font-semibold">$5.00</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Create Ad Dialog */}
      <Dialog open={showCreateAd} onOpenChange={setShowCreateAd}>
        <DialogContent className="max-w-lg">
          <CreateAdForm 
            onSuccess={() => { setShowCreateAd(false); fetchData(); }} 
            onClose={() => setShowCreateAd(false)}
            getHeaders={getHeaders}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * AddFundsForm - Form to add funds to advertiser account
 */
function AddFundsForm({ onSuccess, getHeaders }) {
  const [amount, setAmount] = useState('50');
  const [loading, setLoading] = useState(false);
  
  const presetAmounts = [25, 50, 100, 250];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(amount) < 10) {
      toast.error('Minimum deposit is $10');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/advertising/add-funds?amount=${amount}`, {}, {
        headers: getHeaders()
      });
      toast.success(`$${amount} added to your balance`);
      setAmount('50');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add funds');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {presetAmounts.map((preset) => (
          <Button
            key={preset}
            type="button"
            variant={amount === String(preset) ? 'default' : 'outline'}
            onClick={() => setAmount(String(preset))}
          >
            ${preset}
          </Button>
        ))}
      </div>
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="10"
            step="1"
            className="pl-8"
          />
        </div>
        <Button type="submit" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Funds'}
        </Button>
      </div>
      
      <p className="text-xs text-gray-500">
        Funds are used for impressions and clicks. Minimum deposit: $10
      </p>
    </form>
  );
}

/**
 * CreateAdForm - Form to create a new advertisement
 */
function CreateAdForm({ onSuccess, onClose, getHeaders }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    budget_daily: '10'
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.link_url || !formData.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/advertising/ads`, {
        ...formData,
        budget_daily: parseFloat(formData.budget_daily)
      }, { headers: getHeaders() });
      
      toast.success('Ad created and pending review');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ad');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <DialogHeader>
        <DialogTitle>Create New Advertisement</DialogTitle>
      </DialogHeader>
      
      <form onSubmit={handleSubmit} className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium mb-1 block">Title *</label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Your ad headline"
            maxLength={100}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium mb-1 block">Description *</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of your offer"
            maxLength={500}
            rows={3}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium mb-1 block">Image URL *</label>
          <Input
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://example.com/image.jpg"
          />
          {formData.image_url && (
            <img 
              src={formData.image_url} 
              alt="Preview" 
              className="mt-2 w-20 h-20 object-cover rounded-lg"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
        </div>
        
        <div>
          <label className="text-sm font-medium mb-1 block">Link URL *</label>
          <Input
            value={formData.link_url}
            onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
            placeholder="https://yourwebsite.com/landing"
          />
        </div>
        
        <div>
          <label className="text-sm font-medium mb-1 block">Daily Budget</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              value={formData.budget_daily}
              onChange={(e) => setFormData({ ...formData, budget_daily: e.target.value })}
              min="5"
              step="1"
              className="pl-8"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Minimum $5/day</p>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Ad
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
